import m from "mithril";
import * as pdfJsLib from "pdfjs-dist";
// Configure pdf.js worker for webpack: bundles a same-origin worker chunk
import 'pdfjs-dist/webpack.mjs';
import Requester from "../../shared/request";
import Gateway from "../../shared/app_settings";
import Toasts from "../../shared/toasts";
import i18n from "../../shared/i18n/i18n";
import Loading from "../../shared/loading";
import SessionController from '../../shared/session';

let loading, originalUnit, enrolmentId, documentUnit, exitCallback, linkRegions = [];
let pdfDoc = null,
    totalPageNum = 1,
    currentPageNumber = 1,
    pageRendering = false,
    pageNumPending = null,
    scale = 1.5,
    fitScale = 1.5,
    canvas,
    ctx,
    allPageVisited = false,
    isDocumentAcknowledged = false,
    disableNextPage = false;

function saveAndClose() {
    loading = true;
    Requester.post(Gateway.public + "units/document/" + originalUnit.enrolment.id + "?enrolmentId=" + enrolmentId, {
            isDocumentAcknowledged: isDocumentAcknowledged
        })
        .then(function () {
            resetPageSettings();
            // Clear PDF state on close to ensure fresh start next open
            pdfDoc = null;
            linkRegions = [];
            exitCallback();
        })
        .catch(function (error) {
            const code = (error && error.response && error.response.properties && error.response.properties.errorCode)
                ? error.response.properties.errorCode
                : "UnexpectedError";
            Toasts.add("error", i18n.t("error"), i18n.t(code));
            loading = false;
        });
}

function closeDocument() {
    resetPageSettings();
    // Clear PDF state on close to ensure fresh start next open
    pdfDoc = null;
    linkRegions = [];
    exitCallback();
}

function renderPage(pageNumber) {
    if(pageRendering) {
        pageNumPending = pageNumber;
        return;
    }

    // If we have reached the end disable the forward button
    disableNextPage = currentPageNumber >= totalPageNum

    if(currentPageNumber === totalPageNum) {
        allPageVisited = true;
    }

    // Check if other page is rendering
    if(pageRendering){
        pageNumPending = pageNumber;
    } else {
        pageRendering = true;

        pdfDoc.getPage(pageNumber).then((page) => {
            // On first render of each document, fit the page to the container width
            if (fitScale === null) {
                const naturalViewport = page.getViewport({scale: 1});
                fitScale = Math.min(window.innerWidth / naturalViewport.width, 1.5);
                scale = fitScale > 1.5 ? 1.5 : fitScale;
            }
            let viewport = page.getViewport({scale:scale});
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            let renderContext = {
                canvasContext: ctx,
                viewport: viewport
            };

            let renderTask = page.render(renderContext);

            renderTask.promise.then(() => {
                pageRendering = false;
                renderLinks(page);
                if (pageNumPending !== null) {
                    renderPage(pageNumPending);
                    pageNumPending = null;
                }
            }).catch((error) => {
                console.error("Page render error:", error);
                pageRendering = false;
                Toasts.add("error", i18n.t("error"), i18n.t("UnableToProcessPdf"));
            });
        }).catch((error) => {
            console.error("Get page error:", error);
            pageRendering = false;
            Toasts.add("error", i18n.t("error"), i18n.t("UnableToProcessPdf"));
        });
    }
}

function renderLinks(page) {
    page.getAnnotations().then((annotations) => {
        annotations.forEach((annotation) => {
            if (annotation.subtype === 'Link' && !!annotation.url) {
                renderLink(annotation);
            }
        });
    }).catch((error) => {
        console.error("Get annotations error:", error);
        // Don't show toast for annotation errors as they're non-critical
    });
}

function isMouseOverRegion(mouseX, mouseY, region) {
    return (
        mouseX >= region.x &&
        mouseX <= region.x + region.width &&
        mouseY >= region.y &&
        mouseY <= region.y + region.height
    );
}

function renderLink(annotation) {
    const rect = pdfJsLib.Util.normalizeRect(annotation.rect);
    const canvasHeight = canvas.height;

    const x = rect[0] * scale;
    const y = canvasHeight - rect[3] * scale; // Flip y-axis
    const width = (rect[2] - rect[0]) * scale;
    const height = (rect[3] - rect[1]) * scale;

    linkRegions.push({ x, y, width, height, url: annotation.url });
    canvas.addEventListener('click', function(event) {
        const mouseX = event.offsetX;
        const mouseY = event.offsetY;

        if (isMouseOverRegion(mouseX, mouseY, { x, y, width, height })) {
            if (annotation.url) {
                window.open(annotation.url, '_blank');
            } else if (annotation.action) {
                // Handle actions if required
            }
        }
    });
}

function onPrevPage() {
    if(currentPageNumber <= 1) {
        return;
    }

    currentPageNumber--;
    renderPage(currentPageNumber);
}

function onNextPage() {
    // Cant move forward
    if(currentPageNumber >= totalPageNum || disableNextPage) {
        return;
    }

    currentPageNumber++;
    renderPage(currentPageNumber);
}

function zoomOut() {
    if(scale > 0.5) {
        scale -= 0.1;
        renderPage(currentPageNumber);
    }
}

function zoomIn() {
   if(scale <= 3) {
       scale += 0.1;
       renderPage(currentPageNumber);
   }
}

function reset() {
    scale = fitScale;
    renderPage(currentPageNumber);
}

function loadDocument() {
    loading = true;
    Requester.get(Gateway.public + "units/document/" + originalUnit.id + "/?enrolmentId=" + originalUnit.enrolment.id).then(function (result) {
        documentUnit = result;
        loadPdfDocumentInView(documentUnit?.unit?.documentPath);
        // Safely interpret acknowledgement flag; default to false when missing
        isDocumentAcknowledged = documentUnit?.document?.isAcknowledged === true;
    }).catch(function (error) {
        try { console.error('Document metadata load failed', error); } catch(e) {}
        const code = (error && error.response && error.response.properties && error.response.properties.errorCode)
            ? error.response.properties.errorCode
            : "UnexpectedError";
        Toasts.add("error", i18n.t("error"), i18n.t(code));
    }).then(function () {
        loading = false;
    });
}

function toggleAcknowledgement() {
    isDocumentAcknowledged = !isDocumentAcknowledged;
}

function loadPdfDocumentInView(path){
    loading = true;
    // Always reset pagination and link regions when loading a document
    resetPageSettings();
    linkRegions = [];
    fitScale = null; // recalculate fit-to-width on first render
    const resolvedUrl = encodeURI(path);
    pdfJsLib.getDocument({
        url: resolvedUrl,
        cMapUrl: '/node_modules/pdfjs-dist/cmaps/',
        cMapPacked: true
    }).promise.then((doc) => {
        pdfDoc = doc;
        totalPageNum = doc.numPages;
        // Force start at first page for consistency with production behavior
        currentPageNumber = 1;
        m.redraw();
    }).catch(function (error) {
        // pdfjs-dist errors are plain JavaScript errors, not wrapped in response.properties
        console.error("PDF loading error:", error);
        Toasts.add("error", i18n.t("error"), i18n.t("UnableToProcessPdf"));
    }).then(function () {
        loading = false;
        if (pdfDoc) {
            renderPage(currentPageNumber);
        }
    });
}

function getHeaderName() {
    if (!documentUnit) {
        return null;
    }
    return SessionController.session.identity.name + ": " + documentUnit.unit.name;
}

function resetPageSettings(){
    allPageVisited = false;
    currentPageNumber = 1;
}

function showAcknowledgeCheckbox() {
    return currentPageNumber === totalPageNum && documentUnit.unit && documentUnit.unit.acknowledgeRequired;
}

function canSaveAndClose() {
    return allPageVisited && (isDocumentAcknowledged || !documentUnit.unit.acknowledgeRequired);
}

function acknowledgementPanel() {
    return m(".acknowledge-panel", [
        m(".acknowledge-panel-icon", m("i.icon-check")),
        m(".acknowledge-panel-body", [
            m(".acknowledge-panel-title", i18n.t("reached_end_of_document")),
            m("div.acknowledge-panel-statement", [
                m("input[type=checkbox]", {
                    checked: isDocumentAcknowledged,
                    onchange: toggleAcknowledgement
                }),
                m("span", documentUnit.unit.acknowledgeStatement)
            ]),
            m(".acknowledge-panel-hint", i18n.t("DefaultAcknowledgeStatement"))
        ]),
        m("button.acknowledge-panel-save", {
            class: canSaveAndClose() ? "" : "disabled",
            onclick: saveAndClose
        }, i18n.t("SaveAndClose"))
    ]);
}

const Component_Document_Viewer = {
    oninit: function (vnode) {
        enrolmentId = vnode.attrs.enrolmentId;
        originalUnit = vnode.attrs.unit;
        loadDocument();
        exitCallback = vnode.attrs.callback;
        resetPageSettings();
    },
    oncreate: function () {
        canvas = document.getElementById("pdf_canvas");
        ctx = canvas.getContext("2d");
        canvas.addEventListener('mousemove', function(event) {
            const mouseX = event.offsetX;
            const mouseY = event.offsetY;
            let isHovering = false;
            for (const region of linkRegions) {
                if (isMouseOverRegion(mouseX, mouseY, region)) {
                    isHovering = true;
                    canvas.style.cursor = 'pointer'; // Change cursor to pointer
                    return;
                }
            }
            if (!isHovering) {
                canvas.style.cursor = 'default';
            }
        });
    },
    view: function () {
        return [
            m("#player-bg"),
            m("#player", m("#PdfViewer", [
                documentUnit ? m("div#header", {class: loading ? "disabled" : "" }, [
                    m("div.box-header mt-2 mb-2", [
                        m("div.box-title item", {style: "align-self:flex-center;"}, [
                            m("h4", { class: "ellipsis", title: getHeaderName(), onclick: function() { this.classList.toggle('full-text'); this.classList.toggle('ellipsis'); } }, getHeaderName())
                        ]),
                        m("div.box-controls", [
                            m("span.ml-2", {class:"box-zoom"}, [
                                m("button", {class:"ml-2", onclick: zoomOut}, m("i", {class:"icon-minus"})),
                                m("button", {class:"ml-2", onclick: reset}, Math.round(scale * 100) + '%', m("i", {class:""})),
                                m("button", {class:"ml-2", onclick: zoomIn}, m("i", {class:"icon-plus"}))
                            ]),
                            //Showing disabled buttons when doc has one page
                            totalPageNum === 1 ?
                                m("span.ml-2", {class:"box-page-controls"}, [
                                    m("button.ml-2", {class: "disabled"}, [
                                        m("i", {class:"icon-back"})
                                    ]),
                                    m("button.ml-2", {class: "disabled" }, [
                                        m("i", {class:"icon-forward"})
                                    ])
                                ]) : m("span.ml-2", {class:"box-page-controls"}, [
                                m("button.ml-2", {class: currentPageNumber <= 1 ? "disabled" : "", onclick: onPrevPage}, [
                                    m("i", {class:"icon-back"})
                                ]),
                                m("button.ml-2", {class: disableNextPage ? "disabled" :"", onclick: onNextPage}, [
                                    m("i", {class:"icon-forward"})
                                ])
                            ]),
                            m("span.ml-2 box-info", {class: (allPageVisited && isDocumentAcknowledged && documentUnit.unit.acknowledgeRequired ||
                                    allPageVisited && !documentUnit.unit.acknowledgeRequired)? "" : "disabled"}, [
                                m("a.ml-2 tooltip", {"data-tooltip": i18n.t("download"), href: documentUnit.unit.documentPath, target:"_blank",
                                        download: documentUnit.unit.name + ".pdf" },
                                    m("i", {class: "icon-download"}))
                            ])
                        ]),
                        // When the acknowledgement panel is showing it has its own Save & Close, so the header one is hidden.
                        m("div.box-close item",
                            {class: showAcknowledgeCheckbox() || canSaveAndClose() ? "" : "tooltip tooltip-bottom", "data-tooltip" : totalPageNum > 1 ? i18n.t("ReadLastPage") : ""},
                            showAcknowledgeCheckbox() ? null : m("button.btn-cta", {class: canSaveAndClose() ? "" : "disabled", onclick: saveAndClose}, i18n.t("SaveAndClose")),
                        m("button", {class:"btn-error ml-2", onclick: closeDocument}, "x"),
                    )
                    ])
                ]) : !loading ?
                    m("div.box-header mt-2 mb-2", m("div.box-controls", i18n.t("cant_load")))
                    : m("div.box-header mt-2 mb-2",  m("div.box-controls",i18n.t("loading"))),
                m("section.section-content",{ style: ""}, [
                    m("div.box-scrollable", [
                        m("div.canvasWrapper", [
                            m("canvas", { id: "pdf_canvas"})
                        ])
                    ])
                ]),
                !loading ? m("div.footer", showAcknowledgeCheckbox() ? acknowledgementPanel() : [
                    m("div.box-header.mt-2.mb-2.text-larger", [
                        m("div.box-controls-footer", totalPageNum > 1 && documentUnit.unit.acknowledgeRequired ? m("span.ml-2.mr-2", i18n.t("ReadLastPage")) : null)
                    ])
                ]) : m(Loading)
            ]))
        ];
    }
};
export default Component_Document_Viewer;
